import { useApolloClient } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import {
  getDateRangeText,
  toComparableId,
} from "../../../../components/campaign/B2B2CCampaignDetails/b2b2cCampaignUtils";
import { getB2bCampaigns } from "../../../../graphql/queries/getB2bCampaigns";
import { useStoreGroups } from "../../../../hooks/useStoreGroups";
import SummaryCard from "./SummaryCard";
import "./summaryPage.scss";

const DEFAULT_CAMPAIGNS_OFFSET = 0;
const DEFAULT_CAMPAIGNS_LIMIT = 100;

const SummaryField = ({ label, value }) => (
  <div className="additional-fields-container">
    <div className="field-pair">
      <span className="field-label">{label}</span>
    </div>
    <div className="field-pair-two-column">
      <span className="field-value">{value}</span>
    </div>
  </div>
);

const SummaryB2B2CDetails = ({ data, isViewScreen, editSectionHandler }) => {
  const apolloClient = useApolloClient();
  const { storeGroups } = useStoreGroups();
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const mediaCampaignId = data?.mediaCampaignId;
  const selectedStoreGroups = useMemo(
    () => (Array.isArray(data?.storeGroups) ? data.storeGroups : []),
    [data?.storeGroups]
  );
  const isB2b2cEnabled = Boolean(mediaCampaignId) || selectedStoreGroups.length > 0;

  useEffect(() => {
    if (!mediaCampaignId) {
      setSelectedCampaign(null);
      return undefined;
    }

    let isCancelled = false;

    apolloClient
      .query({
        query: getB2bCampaigns,
        variables: {
          offset: DEFAULT_CAMPAIGNS_OFFSET,
          limit: DEFAULT_CAMPAIGNS_LIMIT,
          validFrom: null,
          validTo: null,
        },
        fetchPolicy: "cache-first",
        errorPolicy: "all",
      })
      .then((result) => {
        if (isCancelled) return;
        const campaigns = result?.data?.getB2bCampaigns || [];
        const match = campaigns.find(
          (campaign) =>
            toComparableId(campaign.id) === toComparableId(mediaCampaignId)
        );
        setSelectedCampaign(match || null);
      })
      .catch(() => {
        if (!isCancelled) {
          setSelectedCampaign(null);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [apolloClient, mediaCampaignId]);

  const storeGroupNameById = useMemo(() => {
    return new Map(
      storeGroups.map((storeGroup) => [
        toComparableId(storeGroup.id),
        storeGroup.displayText,
      ])
    );
  }, [storeGroups]);

  const storeGroupNames = selectedStoreGroups.map(
    (storeGroupId) =>
      storeGroupNameById.get(toComparableId(storeGroupId)) || String(storeGroupId)
  );

  if (!isB2b2cEnabled) {
    return (
      <SummaryCard
        title="B2B2C Campaign"
        isViewScreen={isViewScreen}
        handleEditSection={editSectionHandler}
      >
        <SummaryField label="B2B2C Campaign" value="No" />
      </SummaryCard>
    );
  }

  const selectedCampaignLabel =
    selectedCampaign?.campaignName || mediaCampaignId || "-";
  const selectedCampaignMeta = selectedCampaign
    ? `${selectedCampaign.campaignNo || "-"} | ${getDateRangeText(
        selectedCampaign.validFrom,
        selectedCampaign.validTo
      )}`
    : "";

  return (
    <SummaryCard
      title="B2B2C Campaign"
      isViewScreen={isViewScreen}
      handleEditSection={editSectionHandler}
    >
      <SummaryField label="B2B2C Campaign" value="Yes" />
      <SummaryField
        label="Selected B2B2C Campaign"
        value={
          selectedCampaignMeta
            ? `${selectedCampaignLabel} (${selectedCampaignMeta})`
            : selectedCampaignLabel
        }
      />
      <SummaryField
        label="Store Groups"
        value={storeGroupNames.length > 0 ? storeGroupNames.join(", ") : "-"}
      />
    </SummaryCard>
  );
};

export default SummaryB2B2CDetails;
